import { Brain, Activity, Users, FileText, AlertCircle, HeartPulse, Stethoscope, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';

export function FND() {
  return (
    <div>
      <Hero
        title="Functional Neurologic Disorder"
        description="Expert diagnosis and specialized care for patients with functional neurologic disorders through an integrated approach combining neurological expertise with comprehensive rehabilitation strategies."
        image="https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding FND</h2>
            <p className="text-gray-600 mb-6">
              Functional Neurologic Disorder (FND) is a complex condition where patients experience 
              genuine neurological symptoms that are caused by problems with the functioning of the 
              nervous system rather than structural damage. These symptoms are real and can significantly 
              impact daily life, but they have the potential for improvement with appropriate treatment.
            </p>
            <p className="text-gray-600">
              Our comprehensive approach combines neurological expertise with specialized rehabilitation 
              techniques, addressing both the physical symptoms and underlying factors that contribute 
              to FND. We understand that each patient's experience is unique, and we tailor our 
              treatment plans to individual needs and circumstances.
            </p>
          </div>
          <div className="bg-gradient-cool rounded-lg p-8 shadow-glow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Common FND Symptoms</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Movement Symptoms</h4>
                  <p className="text-gray-600">Tremors, weakness, paralysis, gait problems, and abnormal movements</p>
                </div>
              </li>
              <li className="flex items-start">
                <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Sensory Symptoms</h4>
                  <p className="text-gray-600">Numbness, tingling, pain, and visual/hearing disturbances</p>
                </div>
              </li>
              <li className="flex items-start">
                <HeartPulse className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Episodes & Attacks</h4>
                  <p className="text-gray-600">Non-epileptic seizures, fainting episodes, and panic attacks</p>
                </div>
              </li>
              <li className="flex items-start">
                <AlertCircle className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Cognitive Symptoms</h4>
                  <p className="text-gray-600">Memory issues, concentration problems, and brain fog</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Our Diagnostic Approach</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
                  alt="Comprehensive Assessment"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Comprehensive Assessment</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Detailed neurological examination</li>
                <li>• Symptom pattern analysis</li>
                <li>• Psychological evaluation</li>
                <li>• Functional capacity assessment</li>
                <li>• Impact on daily activities</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80"
                  alt="Specialized Testing"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Specialized Testing</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Advanced neuroimaging</li>
                <li>• Movement analysis</li>
                <li>• Cognitive function testing</li>
                <li>• Autonomic function assessment</li>
                <li>• Quality of life measures</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&q=80"
                  alt="Treatment Planning"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Treatment Planning</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Individualized goal setting</li>
                <li>• Multi-disciplinary coordination</li>
                <li>• Progress monitoring</li>
                <li>• Family involvement</li>
                <li>• Support system assessment</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-healing rounded-lg p-8 mb-16 shadow-glow">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Comprehensive Treatment Program</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Physical Rehabilitation</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Specialized movement retraining</span>
                </li>
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Neuroplasticity-based exercises</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Gradual activity progression</span>
                </li>
                <li className="flex items-start">
                  <Stethoscope className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Symptom management techniques</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Psychological Support</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <Users className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Cognitive behavioral therapy</span>
                </li>
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Stress management training</span>
                </li>
                <li className="flex items-start">
                  <HeartPulse className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Emotional regulation skills</span>
                </li>
                <li className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Coping strategy development</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Specialized Services</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&q=80"
                  alt="Occupational Therapy"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Occupational Therapy</h3>
              <p className="text-gray-600">
                Practical strategies and adaptations for daily activities, work reintegration, 
                and independence enhancement. Includes environmental modifications and assistive 
                device training.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&q=80"
                  alt="Speech & Language Therapy"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Speech & Language Therapy</h3>
              <p className="text-gray-600">
                Support for communication difficulties, cognitive rehabilitation, and swallowing 
                problems. Includes strategies for memory and attention improvement.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80"
                  alt="Support Groups"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Support Groups</h3>
              <p className="text-gray-600">
                Facilitated group sessions for patients and families, providing education, 
                coping strategies, and peer support in a safe, understanding environment.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Begin Your Recovery Journey</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            FND is a treatable condition, and recovery is possible with the right support and 
            treatment approach. Our experienced team is here to help you understand your condition 
            and guide you on your path to improvement.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
            >
              Schedule a Consultation
            </Link>
            <Link
              to="/resources/education"
              className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
            >
              Learn More About FND
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}